import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { vehicleTypes, packageCategories, paymentMethods, getGoogleAuthUrl } from "@/components/delivery";

/**
 * Custom hook for managing local delivery booking state and logic
 * Extracted from LocalDelivery.jsx for better organization
 */
export function useLocalDeliveryBooking(user) {
  const [searchingPartner, setSearchingPartner] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [orderData, setOrderData] = useState(null);
  
  // Form state
  const [pickupAddress, setPickupAddress] = useState("");
  const [pickupContact, setPickupContact] = useState("");
  const [dropAddress, setDropAddress] = useState("");
  const [dropContact, setDropContact] = useState("");
  const [packageCategory, setPackageCategory] = useState("small");
  const [packageWeight, setPackageWeight] = useState("2");
  const [packageDescription, setPackageDescription] = useState("");
  const [vehicleType, setVehicleType] = useState("bike");
  const [paymentMethod, setPaymentMethod] = useState("online");
  
  // Geocoding coordinates
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropCoords, setDropCoords] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  
  // Phone validation state
  const [phonesValid, setPhonesValid] = useState({ pickupPhoneValid: false, dropPhoneValid: false });
  
  // Wallet state
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  
  // Price calculation
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [estimatedDistance, setEstimatedDistance] = useState(null);
  
  const distanceRef = useRef(null);

  // Geocode address using backend API
  const geocodeAddress = useCallback(async (address) => {
    if (!address || address.length < 3) return null;
    try {
      const response = await api.get(`/maps/geocode?address=${encodeURIComponent(address)}`);
      return { lat: response.data.lat, lng: response.data.lng };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Geocoding failed:', error.message);
      }
      return null;
    }
  }, []);

  // Get current location
  const getCurrentLocation = useCallback(async () => {
    setGettingLocation(true);
    try {
      if (!navigator.geolocation) {
        toast.error("Geolocation is not supported by your browser");
        return;
      }
      
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });
      
      const { latitude, longitude } = position.coords;
      setPickupCoords({ lat: latitude, lng: longitude });
      
      // Reverse geocode to get address
      try {
        const geocoder = new window.google.maps.Geocoder();
        const results = await new Promise((resolve, reject) => {
          geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (res, status) => {
            if (status === "OK" && res[0]) resolve(res);
            else reject(new Error("Geocoding failed"));
          });
        });
        setPickupAddress(results[0].formatted_address);
        toast.success("Location detected!");
      } catch {
        setPickupAddress(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        toast.success("Location coordinates detected");
      }
    } catch {
      toast.error("Could not get your location. Please enter address manually.");
    } finally {
      setGettingLocation(false);
    }
  }, []);

  // Geocode pickup address when it changes
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (pickupAddress && pickupAddress.length > 5 && !pickupCoords) {
        const coords = await geocodeAddress(pickupAddress + ", India");
        if (coords) setPickupCoords(coords);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [pickupAddress, pickupCoords, geocodeAddress]);

  // Geocode drop address when it changes
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (dropAddress && dropAddress.length > 5 && !dropCoords) {
        const coords = await geocodeAddress(dropAddress + ", India");
        if (coords) setDropCoords(coords);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [dropAddress, dropCoords, geocodeAddress]);

  // Reset coords when addresses change
  useEffect(() => {
    setPickupCoords(null);
  }, [pickupAddress]);
  
  useEffect(() => {
    setDropCoords(null);
  }, [dropAddress]);

  // Fetch wallet balance on mount
  useEffect(() => {
    if (user) {
      api.get("/wallet/balance")
        .then(res => setWalletBalance(res.data.wallet_balance || 0))
        .catch(() => setWalletBalance(0));
    }
  }, [user]);

  // Calculate amount to pay after wallet
  const walletDeduction = useMemo(() => {
    if (!useWallet || !estimatedPrice || paymentMethod !== 'online') return 0;
    return Math.min(walletBalance, estimatedPrice);
  }, [useWallet, walletBalance, estimatedPrice, paymentMethod]);

  const amountToPay = useMemo(() => {
    if (!estimatedPrice) return 0;
    return Math.max(0, estimatedPrice - walletDeduction);
  }, [estimatedPrice, walletDeduction]);

  // Auto-suggest vehicle based on package category
  useEffect(() => {
    const category = packageCategories.find(c => c.id === packageCategory);
    if (category?.suggestedVehicle) {
      setVehicleType(category.suggestedVehicle);
    }
  }, [packageCategory]);

  // Calculate price when inputs change
  const calculatePrice = useCallback(() => {
    if (!pickupAddress || !dropAddress) {
      setEstimatedPrice(null);
      setEstimatedDistance(null);
      return;
    }
    
    // Use existing distance or generate new one
    if (!distanceRef.current) {
      distanceRef.current = Math.floor(Math.random() * 15) + 3; // 3-18 km
    }
    const distance = distanceRef.current;
    setEstimatedDistance(distance);
    
    const vehicle = vehicleTypes.find(v => v.id === vehicleType);
    const payment = paymentMethods.find(p => p.id === paymentMethod);
    
    if (vehicle) {
      let price = vehicle.basePrice + (distance * vehicle.perKmPrice);
      
      // Payment method adjustment
      if (payment?.discount) {
        price = price * (1 - payment.discount / 100);
      } else if (payment?.extraCharge) {
        price += payment.extraCharge;
      }
      
      setEstimatedPrice(Math.round(price));
    }
  }, [pickupAddress, dropAddress, vehicleType, paymentMethod]);

  // Reset distance when addresses change
  useEffect(() => {
    distanceRef.current = null;
  }, [pickupAddress, dropAddress]);

  useEffect(() => {
    calculatePrice();
  }, [calculatePrice]);

  // Form validation
  const isFormValid = pickupAddress && pickupContact && dropAddress && dropContact && 
    phonesValid.pickupPhoneValid && phonesValid.dropPhoneValid;

  // Handle booking submission
  const handleBookNow = async () => {
    if (!user) {
      toast.error("Please sign in to book delivery");
      window.location.href = getGoogleAuthUrl();
      return;
    }

    if (!pickupAddress || !pickupContact || !dropAddress || !dropContact) {
      toast.error("Please fill all required fields");
      return;
    }

    // Validate phone numbers
    if (!phonesValid.pickupPhoneValid || !phonesValid.dropPhoneValid) {
      toast.error("Please enter valid 10-digit mobile numbers for pickup and drop");
      return;
    }

    setSearchingPartner(true);
    
    try {
      const response = await api.post("/local-delivery", {
        pickup_points: [{
          address: pickupAddress,
          contact: pickupContact,
          landmark: "",
          name: "",
          latitude: pickupCoords?.lat || null,
          longitude: pickupCoords?.lng || null
        }],
        drop_points: [{
          address: dropAddress,
          contact: dropContact,
          landmark: "",
          name: "",
          latitude: dropCoords?.lat || null,
          longitude: dropCoords?.lng || null
        }],
        package_category: packageCategory,
        package_weight: parseFloat(packageWeight) || 2,
        package_description: packageDescription,
        vehicle_type: vehicleType,
        delivery_type: "express",
        payment_method: paymentMethod,
        use_wallet: useWallet && paymentMethod === 'online',
        wallet_amount: walletDeduction
      });
      
      setOrderData({
        id: response.data.id,
        price: response.data.final_price,
        walletUsed: response.data.wallet_used || 0,
        amountPaid: response.data.amount_to_pay || response.data.final_price,
        eta: response.data.eta_mins,
        partner: response.data.partner_id ? true : false
      });
      setShowConfirmation(true);
      
      // Update wallet balance after usage
      if (response.data.wallet_used > 0) {
        setWalletBalance(prev => prev - response.data.wallet_used);
      }
      
      toast.success("Delivery booked successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book delivery");
    } finally {
      setSearchingPartner(false);
    }
  };

  const resetForm = () => {
    setPickupAddress("");
    setPickupContact("");
    setDropAddress("");
    setDropContact("");
    setPackageCategory("small");
    setPackageWeight("2");
    setPackageDescription("");
    setVehicleType("bike");
    setPaymentMethod("online");
    setShowConfirmation(false);
    setOrderData(null);
  };

  return {
    // Form state
    pickupAddress, setPickupAddress,
    pickupContact, setPickupContact,
    dropAddress, setDropAddress,
    dropContact, setDropContact,
    packageCategory, setPackageCategory,
    packageWeight, setPackageWeight,
    packageDescription, setPackageDescription,
    vehicleType, setVehicleType,
    paymentMethod, setPaymentMethod,
    
    // Coords
    pickupCoords, setPickupCoords,
    dropCoords, setDropCoords,
    gettingLocation,
    getCurrentLocation,
    
    // Phone validation
    phonesValid, setPhonesValid,
    
    // Wallet
    walletBalance, useWallet, setUseWallet,
    walletDeduction, amountToPay,
    
    // Price
    estimatedPrice, estimatedDistance,
    
    // Booking state
    searchingPartner,
    showConfirmation, setShowConfirmation,
    orderData,
    
    // Actions
    handleBookNow,
    resetForm,
    isFormValid
  };
}
